// Avatar Eteka — visage vectoriel stylisé avec animation de lèvres et de yeux.
//
// L'illustration reste minimaliste (icône reconnaissable à 64×64 px) mais
// gagne en personnalité : ovale plus naturel, yeux avec iris et reflet,
// sourcils, cils, blush sur les joues, lèvres pleines, mèches de cheveux.
//
// Animation :
//   - speak(durationMs) : alterne 5 visemes (positions de bouche) toutes les
//     ~110 ms pour simuler une parole, sans synthèse audio.
//   - blink : clignement aléatoire toutes les 2.5–6 s (transform sur les yeux).

export interface Avatar {
  el: HTMLElement;
  speak: (durationMs: number) => void;
  stop: () => void;
}

// Positions de bouche : (x1,y1) Q (cx,cy) (x2,y2). Centre x ≈ 60.
const MOUTH_CLOSED = 'M 52 80 Q 60 82.5 68 80';
const MOUTH_SMILE = 'M 51 79 Q 60 84 69 79';
const MOUTH_HALF = 'M 52 79 Q 60 86 68 79';
const MOUTH_OPEN = 'M 52 78 Q 60 90 68 78';
const MOUTH_OPEN_WIDE = 'M 50 78 Q 60 93 70 78';

const VISEMES = [MOUTH_CLOSED, MOUTH_HALF, MOUTH_OPEN, MOUTH_OPEN_WIDE, MOUTH_HALF, MOUTH_SMILE];

export function createAvatar(): Avatar {
  const wrap = document.createElement('div');
  wrap.className = 'eteka-avatar';
  wrap.innerHTML = `
    <svg viewBox="0 0 120 120" width="64" height="64" aria-label="Eteka">
      <defs>
        <radialGradient id="eteka-face" cx="0.5" cy="0.42" r="0.55">
          <stop offset="0%" stop-color="#fde4d2"/>
          <stop offset="65%" stop-color="#f4c8a8"/>
          <stop offset="100%" stop-color="#e0a884"/>
        </radialGradient>
        <linearGradient id="eteka-hair" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#3d2719"/>
          <stop offset="60%" stop-color="#2a1810"/>
          <stop offset="100%" stop-color="#1a0f08"/>
        </linearGradient>
        <radialGradient id="eteka-blush" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stop-color="#e88a8a" stop-opacity="0.55"/>
          <stop offset="100%" stop-color="#e88a8a" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="eteka-iris" cx="0.4" cy="0.4" r="0.7">
          <stop offset="0%" stop-color="#7a5638"/>
          <stop offset="100%" stop-color="#3a2418"/>
        </radialGradient>
        <linearGradient id="eteka-lips" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#e07585"/>
          <stop offset="100%" stop-color="#a53b4a"/>
        </linearGradient>
      </defs>

      <!-- Cheveux arrière (silhouette enveloppante) -->
      <path d="M 22 60 Q 18 32 36 18 Q 52 11 68 14 Q 92 19 96 48 Q 99 70 92 88 Q 87 92 83 86 Q 84 70 84 58 Q 84 38 65 36 Q 45 35 38 56 Q 36 72 36 86 Q 32 92 27 88 Q 18 78 22 60 Z"
            fill="url(#eteka-hair)"/>

      <!-- Visage ovale -->
      <ellipse cx="60" cy="62" rx="25" ry="29" fill="url(#eteka-face)"/>

      <!-- Mèches devant (cadre le visage) -->
      <path d="M 38 48 Q 40 36 50 32 Q 48 42 44 52 Q 40 52 38 48 Z" fill="url(#eteka-hair)"/>
      <path d="M 82 48 Q 80 36 70 32 Q 72 42 76 52 Q 80 52 82 48 Z" fill="url(#eteka-hair)"/>

      <!-- Blush sur les joues -->
      <ellipse cx="44" cy="69" rx="6" ry="3.5" fill="url(#eteka-blush)"/>
      <ellipse cx="76" cy="69" rx="6" ry="3.5" fill="url(#eteka-blush)"/>

      <!-- Sourcils -->
      <path d="M 44 51 Q 49.5 48 55 51" stroke="#2a1810" stroke-width="2.2"
            fill="none" stroke-linecap="round"/>
      <path d="M 65 51 Q 70.5 48 76 51" stroke="#2a1810" stroke-width="2.2"
            fill="none" stroke-linecap="round"/>

      <!-- Yeux : groupes pour pouvoir animer le clignement -->
      <g class="eteka-eye-l">
        <ellipse cx="49.5" cy="59" rx="4.2" ry="3" fill="#ffffff"/>
        <circle cx="49.5" cy="59" r="2.6" fill="url(#eteka-iris)"/>
        <circle cx="49.5" cy="59" r="1.1" fill="#000000"/>
        <circle cx="50.4" cy="58.1" r="0.9" fill="#ffffff"/>
        <!-- Cils -->
        <path d="M 45.5 56.5 Q 49.5 54.5 53.5 56.5" stroke="#1a0f08" stroke-width="1.2"
              fill="none" stroke-linecap="round"/>
        <path d="M 45.5 56.5 L 44.7 55" stroke="#1a0f08" stroke-width="0.9" stroke-linecap="round"/>
        <path d="M 53.5 56.5 L 54.3 55" stroke="#1a0f08" stroke-width="0.9" stroke-linecap="round"/>
      </g>
      <g class="eteka-eye-r">
        <ellipse cx="70.5" cy="59" rx="4.2" ry="3" fill="#ffffff"/>
        <circle cx="70.5" cy="59" r="2.6" fill="url(#eteka-iris)"/>
        <circle cx="70.5" cy="59" r="1.1" fill="#000000"/>
        <circle cx="71.4" cy="58.1" r="0.9" fill="#ffffff"/>
        <!-- Cils -->
        <path d="M 66.5 56.5 Q 70.5 54.5 74.5 56.5" stroke="#1a0f08" stroke-width="1.2"
              fill="none" stroke-linecap="round"/>
        <path d="M 66.5 56.5 L 65.7 55" stroke="#1a0f08" stroke-width="0.9" stroke-linecap="round"/>
        <path d="M 74.5 56.5 L 75.3 55" stroke="#1a0f08" stroke-width="0.9" stroke-linecap="round"/>
      </g>

      <!-- Nez (suggéré, deux courbes discrètes) -->
      <path d="M 58 65 Q 57 71 59.5 73" stroke="#c89178" stroke-width="0.9"
            fill="none" stroke-linecap="round" opacity="0.8"/>
      <path d="M 62 65 Q 63 71 60.5 73" stroke="#c89178" stroke-width="0.9"
            fill="none" stroke-linecap="round" opacity="0.8"/>

      <!-- Bouche : lèvres pleines, animée -->
      <path class="eteka-mouth" d="${MOUTH_SMILE}"
            stroke="#a53b4a" stroke-width="2.4"
            fill="url(#eteka-lips)" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Reflet sur la lèvre inférieure -->
      <path d="M 55 81.5 Q 60 83 65 81.5" stroke="#ffd5dc" stroke-width="0.6"
            fill="none" opacity="0.6" stroke-linecap="round"/>

      <!-- Boucles d'oreilles -->
      <circle cx="32" cy="72" r="2" fill="#e8c659"/>
      <circle cx="88" cy="72" r="2" fill="#e8c659"/>
      <circle cx="32" cy="72" r="0.8" fill="#fff5d6" opacity="0.7"/>
      <circle cx="88" cy="72" r="0.8" fill="#fff5d6" opacity="0.7"/>
    </svg>
  `;

  const mouth = wrap.querySelector<SVGPathElement>('.eteka-mouth')!;
  const eyeL = wrap.querySelector<SVGGElement>('.eteka-eye-l')!;
  const eyeR = wrap.querySelector<SVGGElement>('.eteka-eye-r')!;

  let speakInterval: number | null = null;
  let blinkTimer: number | null = null;

  function setMouth(d: string) {
    mouth.setAttribute('d', d);
  }

  function speak(durationMs: number) {
    stop();
    let i = 0;
    setMouth(VISEMES[0]!);
    speakInterval = window.setInterval(() => {
      i = (i + 1) % VISEMES.length;
      setMouth(VISEMES[i]!);
    }, 110);
    window.setTimeout(() => {
      stop();
      setMouth(MOUTH_SMILE); // au repos, léger sourire
    }, durationMs);
  }

  function stop() {
    if (speakInterval != null) {
      window.clearInterval(speakInterval);
      speakInterval = null;
    }
    setMouth(MOUTH_SMILE);
  }

  function scheduleBlink() {
    const next = 2500 + Math.random() * 3500;
    blinkTimer = window.setTimeout(() => {
      eyeL.style.transform = 'scaleY(0.08)';
      eyeR.style.transform = 'scaleY(0.08)';
      eyeL.style.transformOrigin = '49.5px 59px';
      eyeR.style.transformOrigin = '70.5px 59px';
      window.setTimeout(() => {
        eyeL.style.transform = '';
        eyeR.style.transform = '';
        scheduleBlink();
      }, 140);
    }, next);
  }
  scheduleBlink();

  return { el: wrap, speak, stop };
}

/** Estime la durée de parole pour un texte (≈ 35 ms / caractère, borné). */
export function estimateSpeechDuration(text: string): number {
  return Math.min(Math.max(text.length * 35, 700), 5000);
}
